import { useState } from 'react';
import get from 'lodash/get';
import { useApolloClient } from '@apollo/client';
import { grades as gradeOptions } from '@/pages/AccountInformation/AccountInformation.constants';
import { GradeChangeInput, Grade } from '@/pages/AccountInformation/AccountInformation.types';
import { GET_PROFILE } from '@/graphql/queries';
import { ApolloClientName } from '@/config/apolloClient';
import { GetMyProfileResponse, LinkedAccount, Hierarchy } from '@/graphql/types';
import { userInformationVar } from '@/graphql/state';
import { useEffectOnMount } from './useEffectOnMount';

export type ChildNewModel = {
  institutionType: string;
  institutionId: string;
  institutionName: string;
  hierarchyType: string;
  hierarchyId: string;
  hierarchyName: string;
  gradeLevel: string | null;
  schoolYear: string;
  schoolType: string;
  childUid: string;
  community: string | null;
  postalCode: string;
  status: string;
  grades?: GradeChangeInput[] | null;
  institutionGradeLevel: string;
  wasAdded?: boolean;
  wasChanged?: boolean;
};

export type Profile = {
  loading: boolean;
  userId: number | null;
  managedBy: string;
  firstName: string;
  lastName: string;
  email: string;
  scopes: string[];
  hierarchy: Hierarchy | null;
  linkedAccounts: LinkedAccount[] | null;
  children: ChildNewModel[] | null;
  groupMembershps: any;
  revertBlocked: boolean;
  updateLinkedAccounts: (accounts: LinkedAccount[]) => void;
  updateChildren: (children: ChildNewModel[]) => void;
  removeChild: (childUid: string) => void;
  updateChild: (childUid: string, changes: object) => void;
  updateEmail: (email: string) => void;
  setManagedBy: (manageBy: string) => void;
  setRevertBlocked: (revertBlocked: boolean) => void;
  getActiveChildren: () => ChildNewModel[];
  getPendingChildren: () => ChildNewModel[];
};

const useProfile = (): Profile => {
  const [loading, setLoading] = useState(true);
  const client = useApolloClient();
  const [userId, setUserId] = useState<number | null>(null);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [managedBy, setManagedBy] = useState<string>('');
  const [hierarchy, setHierarchy] = useState<Hierarchy | null>(null);
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[] | null>(null);
  const [children, setChildren] = useState<ChildNewModel[] | null>(null);
  const [groupMembershps, setGroupMemberships] = useState<any>(null);
  const [scopes, setScopes] = useState<string[]>([]);
  const [revertBlocked, setRevertBlocked] = useState<boolean>(true);

  const getActiveChildren = (): ChildNewModel[] => children!.filter(c => c.status === 'active');

  const getPendingChildren = (): ChildNewModel[] => children!.filter(c => c.status === 'pending');

  const mapChildren = (childrenList: ChildNewModel[]): ChildNewModel[] => {
    let grades: Array<Grade>;
    return childrenList.map(child => {
      grades = [];
      if (child.institutionGradeLevel) {
        const [lowGrade, highGrade] = child.institutionGradeLevel.split('-');
        const lowIndex = gradeOptions.findIndex(g => g.description === lowGrade);
        const highIndex = gradeOptions.findIndex(g => g.description === highGrade);
        grades = gradeOptions.slice(lowIndex, highIndex + 1);
      }

      return {
        ...child,
        grades,
        wasAdded: false,
        wasChanged: false,
      };
    });
  };

  const getProfile = async () => {
    try {
      setLoading(true);
      const { data } = await client.query<GetMyProfileResponse>({
        query: GET_PROFILE,
        context: {
          clientName: ApolloClientName.AccountsBff,
        },
        fetchPolicy: 'network-only',
      });
      if (data?.profileNewModel) {
        const {
          userId,
          firstName: fname,
          lastName: lname,
          managedBy: profileManagedBy,
          email: userEmail,
          linkedAccounts: userAccounts,
          children: childrenList,
          hierarchy: hierarchyParams,
          academicGroupMemberships: memberships,
          scopes: userScopes,
          revertBlocked: isRevertBlocked,
        } = data.profileNewModel;
        const response = data;
        const success = get(response, 'profileNewModel.success', true);

        const childrenMapped = mapChildren(childrenList || []);

        if (success) {
          userInformationVar({
            email: userEmail,
            firstName: fname,
            lastName: lname,
            id: Number(userId),
          });
          setUserId(Number(userId));
          setFirstName(fname);
          setLastName(lname);
          setEmail(userEmail);
          setLinkedAccounts(userAccounts);
          setChildren(childrenMapped);
          setManagedBy(profileManagedBy);
          setGroupMemberships(memberships);
          setHierarchy(hierarchyParams);
          setScopes(userScopes);
          setRevertBlocked(isRevertBlocked);
        }
      }
    } catch (e) {
      // checkGqlError(e);
      console.log('error getting user profile', e);
    } finally {
      setLoading(false);
    }
  };

  useEffectOnMount(() => {
    getProfile();
  });

  return {
    loading,
    userId,
    firstName,
    lastName,
    managedBy,
    email,
    hierarchy,
    linkedAccounts,
    children,
    groupMembershps,
    scopes,
    setManagedBy,
    revertBlocked,
    setRevertBlocked,
    getActiveChildren,
    getPendingChildren,
    updateLinkedAccounts(accounts: LinkedAccount[]) {
      if (Array.isArray(accounts) && accounts.length) {
        setLinkedAccounts(accounts);
      }
    },
    updateChildren(childrenList: ChildNewModel[]) {
      if (Array.isArray(childrenList) && childrenList.length) {
        setChildren(childrenList);
      }
    },
    updateChild(childUid: string, changes: object) {
      if (children) {
        const index = children!.findIndex(c => c.childUid === childUid);
        const updatedChild: ChildNewModel = {
          ...children![index],
          ...changes,
        };
        const newChildren = [
          ...children.slice(0, index),
          updatedChild,
          ...children.slice(index + 1),
        ];
        setChildren(newChildren);
      }
    },
    removeChild(childUid: string) {
      setChildren(children!.filter(c => c.childUid !== childUid));
    },
    updateEmail(e) {
      setEmail(e);
    },
  };
};

export default useProfile;
